'use client';

import { VpAccordion, VpAccordionItem, VpAccordionItemHeader, VpAccordionItemHeaderIcon, VpAccordionItemHeaderLabel, VpAccordionItemPanel, VpIcon, VpLink } from '@vtmn-play/react/headless';
import { VtmnToggle } from '@vtmn/react';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { InfoBar } from '@/app/components/InfoBar/InfoBar';
import { NotificationAuthorizations } from '@/types/user/enums';
import appConst from '~/const/appConst';

export const NotificationSwitch = ({
  userSubscription,
  type,
  handleUpdateSubscription,
}: {
  userSubscription: NotificationAuthorizations[];
  type: NotificationAuthorizations;
  handleUpdateSubscription: (subscriptionList: NotificationAuthorizations[]) => void;
}) => {
  const t = useTranslations('SETTINGS.NOTIFICATION');

  const { EVENT, MESSAGE, NEW_SUBJECTS, SURVEY } = NotificationAuthorizations;

  const [isChecked, setIsChecked] = useState(false);
  const [isSurveyChecked, setIsSurveyChecked] = useState(false);
  const [isEventChecked, setIsEventChecked] = useState(false);
  const [openedPanel, setOpenedPanel] = useState<string[]>([]);

  const href = `${appConst.staticRoutes.profile}/favourite-sports`;

  const isNewSubjects = type === NEW_SUBJECTS;
  const isMessage = type === MESSAGE;

  const notificationTitleLabel = t(`${type}.TITLE` as any);
  const notificationDescriptionLabel = t(
    `${type}.DESCRIPTION` as any,
  );
  const notificationFavoriteSportsLabel = t(
    'FAVORITE_SPORTS',
  );

  const handleClick = (notificationAuthorizationType: NotificationAuthorizations) => {
    const isSurvey = notificationAuthorizationType === SURVEY;
    const isEvent = notificationAuthorizationType === EVENT;

    const isAuthorizationChecked
      = (isSurvey && isSurveyChecked)
        || (isEvent && isEventChecked)
        || (!isSurvey && !isEvent && isChecked);

    const typesToUpdate
      = notificationAuthorizationType === NEW_SUBJECTS
        ? [SURVEY, EVENT]
        : [NotificationAuthorizations[notificationAuthorizationType]];

    const updatedData = isAuthorizationChecked
      ? userSubscription.filter(
          notificationAuthorization =>
            !typesToUpdate.includes(notificationAuthorization),
        )
      : [...userSubscription, ...typesToUpdate];

    handleUpdateSubscription(updatedData);

    if (isSurvey)
      setIsSurveyChecked(!isSurveyChecked);
    else if (isEvent)
      setIsEventChecked(!isEventChecked);
    else setIsChecked(!isChecked);
  };

  useEffect(() => {
    if (isNewSubjects) {
      const hasSurveyAuthorized = userSubscription.includes(
        SURVEY,
      );
      const hasEventAuthorized = userSubscription.includes(
        EVENT,
      );

      setIsChecked(hasSurveyAuthorized || hasEventAuthorized);
      setIsSurveyChecked(hasSurveyAuthorized);
      setIsEventChecked(hasEventAuthorized);
    } else if (userSubscription.includes(type)) {
      setIsChecked(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewSubjects, `${userSubscription}`, type]);

  return (
    <>
      <div className="notification-switch">
        <div className="notification-switch__labels">
          <h2 className="vp-title-s">
            {notificationTitleLabel}
          </h2>
          <span className="vp-body-s notification-switch__description">
            {notificationDescriptionLabel}
          </span>
          {isNewSubjects && (
            <span className="notification-switch__favorite-sports">
              <VpLink href={href}>
                <span className="vp-body-s notification-switch__favorite-sports__link">
                  {notificationFavoriteSportsLabel}
                </span>
              </VpLink>
              {' '}
              <VpIcon
                name="settings"
                size={16}
              />
            </span>
          )}
        </div>
        <VtmnToggle
          aria-label={`${notificationTitleLabel} ${notificationDescriptionLabel}${
            isNewSubjects ? ` ${notificationFavoriteSportsLabel}` : ''
          }`}
          identifier={notificationTitleLabel}
          checked={isChecked}
          onClick={() => handleClick(type)}
        />
      </div>
      {isNewSubjects && (
        <VpAccordion
          variant="secondary"
          value={openedPanel}
          onValueChange={setOpenedPanel}
        >
          <VpAccordionItem value="notification-subjects">
            <VpAccordionItemHeader>
              <VpAccordionItemHeaderLabel>
                {t('SUBJECTS.TITLE')}
              </VpAccordionItemHeaderLabel>
              <VpAccordionItemHeaderIcon />
            </VpAccordionItemHeader>
            <VpAccordionItemPanel>
              <div className="notification-switch__subjects profile__fieldgroup">
                <div className="notification-switch__subjects__toggle">
                  <span className="vp-body-m">
                    {t('SUBJECTS.SURVEY')}
                  </span>
                  <VtmnToggle
                    aria-label={t('SUBJECTS.SURVEY')}
                    identifier={t('SUBJECTS.SURVEY')}
                    checked={isSurveyChecked}
                    onClick={() => handleClick(SURVEY)}
                  />
                </div>
                <div className="notification-switch__subjects__toggle">
                  <span className="vp-body-m">
                    {t('SUBJECTS.EVENT')}
                  </span>
                  <VtmnToggle
                    aria-label={t('SUBJECTS.EVENT')}
                    identifier={t('SUBJECTS.EVENT')}
                    checked={isEventChecked}
                    onClick={() => handleClick(EVENT)}
                  />
                </div>
              </div>
            </VpAccordionItemPanel>
          </VpAccordionItem>
        </VpAccordion>
      )}
      {isMessage && (
        <InfoBar content={t('MESSAGE.INFORMATION')} />
      )}
    </>
  );
};
